import { useEffect, useState } from "react";
import { fetchSampleCollectionWorklist } from "../../../utils/api";
import { useLocation, useNavigate } from "react-router-dom";
import { formatDateTime } from "utils/formatDateTime";
import { Box, Typography, Chip, Button, Divider, Tabs, Tab, IconButton, Tooltip, Stack } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DateRangeFilter from "../../../components/DateRangeFilter";
import StyledDataGrid from "../../../components/StyledDataGrid";
import CustomIcon from "components/CustomIcon";
import PageHeader from "../../../components/PageHeader";
import WorkspacePage from "../../../components/WorkspacePage";
import { isCheckedIn } from "../../../constants/checkInStatus";
import { getStatusChipStyle, BASE_CHIP_SX } from "../../../constants/statusColors";
import {
  readReturnDateFilters,
  writeStoredDateFilters,
  getDefaultDateFiltersTodayToNextMonth,
} from "../../../utils/dateFilters";
import { BIOBANK_SUB_ROUTES } from "../../../constants/routes";

const SAMPLE_COLLECTION_DATE_FILTER_STORAGE_KEY = "sampleCollectionDateFilters";
const FOLLOW_UP_COLLECTION_DATE_FILTER_STORAGE_KEY = "followUpSampleCollectionDateFilters";

const getDefaultSampleCollectionDateFilters = () => getDefaultDateFiltersTodayToNextMonth();

const getDefaultFollowUpCollectionDateFilters = () => getDefaultDateFiltersTodayToNextMonth();

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const FOLLOW_UP_CODE_PATTERN = /-H\d+$/i;

const buildDisplayFollowUpCode = (rootSampleId, milestoneNumber) => {
  if (!rootSampleId || milestoneNumber === null || milestoneNumber === undefined) return "";
  const prefix = /-H$/i.test(rootSampleId) ? rootSampleId.slice(0, -2) : rootSampleId;
  return `${prefix}-H${milestoneNumber}`;
};

const getFollowUpDisplayCode = (encounterData) => {
  const encounter = encounterData?.encounter || {};
  const followUp = encounterData?.followUp || encounterData?.mitraFollowUp || {};
  if (encounter.displayFollowUpCode) return encounter.displayFollowUpCode;
  if (encounter.followUpCode) return encounter.followUpCode;
  if (followUp.displayFollowUpCode) return followUp.displayFollowUpCode;
  const builtCode = buildDisplayFollowUpCode(followUp.rootSampleId, followUp.milestoneNumber);
  if (builtCode) return builtCode;
  if (encounter.followUpId && !UUID_PATTERN.test(encounter.followUpId) && FOLLOW_UP_CODE_PATTERN.test(encounter.followUpId)) {
    return encounter.followUpId;
  }
  return "";
};

const normalizeFollowUpEncounterData = (encounterData) => {
  const encounter = encounterData?.encounter || {};
  const followUp = encounterData?.followUp || encounterData?.mitraFollowUp || {};
  const displayFollowUpCode = getFollowUpDisplayCode(encounterData);

  return {
    ...encounterData,
    encounter: {
      ...encounter,
      followUpId: encounter.followUpId || followUp.followUpId,
      displayFollowUpCode,
      followUpCode: displayFollowUpCode,
      rootSampleId: followUp.rootSampleId || encounter.rootSampleId,
    },
  };
};

const SampleCollection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const shouldRestoreDateFilters = Boolean(location?.state?.sampleCollected);
  const [activeView, setActiveView] = useState("all"); // "all" or "followup"
  const [allRows, setAllRows] = useState([]);
  const [followUpRows, setFollowUpRows] = useState([]);
  const [loadingAll, setLoadingAll] = useState(true);
  const [loadingFollowUp, setLoadingFollowUp] = useState(false);
  const [defaultDateFilters] = useState(getDefaultSampleCollectionDateFilters);
  const [defaultFollowUpDateFilters] = useState(getDefaultFollowUpCollectionDateFilters);
  const [dateFilters, setDateFilters] = useState(() =>
    readReturnDateFilters(
      SAMPLE_COLLECTION_DATE_FILTER_STORAGE_KEY,
      getDefaultSampleCollectionDateFilters(),
      shouldRestoreDateFilters,
    )
  );
  const [followUpDateFilters, setFollowUpDateFilters] = useState(() =>
    readReturnDateFilters(
      FOLLOW_UP_COLLECTION_DATE_FILTER_STORAGE_KEY,
      getDefaultFollowUpCollectionDateFilters(),
      shouldRestoreDateFilters,
    )
  );

  const [paginationAll, setPaginationAll] = useState({ page: 0, pageSize: 25, total: 0 });
  const [paginationFollowUp, setPaginationFollowUp] = useState({ page: 0, pageSize: 25, total: 0 });
  const [sortModelAll, setSortModelAll] = useState([]);
  const [sortModelFollowUp, setSortModelFollowUp] = useState([]);

  useEffect(() => {
    if (location?.state?.sampleCollected) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location?.state?.sampleCollected, navigate]);

  // Extract milestone code from display follow-up code (e.g., "0000000-H1" -> "H1")
  const getMilestoneCode = (displayFollowUpCode) => {
    if (!displayFollowUpCode) return '';
    return displayFollowUpCode.split('-').pop() || '';
  };

  // Format milestone for display (e.g., "H1" -> "1")
  const formatMilestone = (milestoneCode) => {
    if (!milestoneCode) return '';
    // Remove 'H' prefix and return just the number
    return milestoneCode.replace(/^H/, '');
  };

  // Fetch all encounters
  useEffect(() => {
    if (activeView !== "all") return;

    const fetchAllData = async () => {
      try {
        setLoadingAll(true);
        const request = {
          collectionType: "ALL",
          checkInDateFrom: dateFilters.encounterDateFrom,
          checkInDateTo: dateFilters.encounterDateTo,
          page: paginationAll.page,
          size: paginationAll.pageSize,
        };
        if (sortModelAll.length > 0) {
          request.sortBy = sortModelAll[0].field;
          request.sortDirection = sortModelAll[0].sort.toUpperCase();
        }

        const response = await fetchSampleCollectionWorklist(request);
        const encountersData = response.content || [];
        setPaginationAll((prev) => ({ ...prev, total: response.totalElements || 0 }));

        const processedRows = encountersData
          .map((encounterData, index) => {
            const normalizedEncounterData = normalizeFollowUpEncounterData(encounterData);
            const { encounter, patient } = normalizedEncounterData;

            if (!encounter || !patient) {
              console.warn("Skipping invalid encounter or patient data");
              return null;
            }

            return {
              id: encounter.encounterId || `encounter-${index}-${Date.now()}`,
              patientName: `${patient.firstName || "Unknown"} ${patient.lastName || "Patient"}`,
              dateTime: formatDateTime(encounter.checkInDateTime),
              encounterType: encounter.encounterType,
              followUpId: encounter.followUpId,
              displayFollowUpCode: encounter.displayFollowUpCode,
              checkInStatus: encounter.checkInStatus || "NOT_CHECKED_IN",
              isCollected: normalizedEncounterData.isCollected || false,
              encounterData: normalizedEncounterData,
            };
          })
          .filter((row) => row !== null);

        setAllRows(processedRows);
      } catch (error) {
        console.error("Error fetching all encounters:", error);
        setAllRows([]);
      } finally {
        setLoadingAll(false);
      }
    };
    fetchAllData();
  }, [activeView, dateFilters, paginationAll.page, paginationAll.pageSize, sortModelAll]);

  // Fetch follow-up encounters
  useEffect(() => {
    if (activeView !== "followup") return;

    const fetchFollowUpData = async () => {
      try {
        setLoadingFollowUp(true);
        const request = {
          collectionType: "FOLLOW_UP",
          checkInDateFrom: followUpDateFilters.encounterDateFrom,
          checkInDateTo: followUpDateFilters.encounterDateTo,
          page: paginationFollowUp.page,
          size: paginationFollowUp.pageSize,
        };
        if (sortModelFollowUp.length > 0) {
          request.sortBy = sortModelFollowUp[0].field;
          request.sortDirection = sortModelFollowUp[0].sort.toUpperCase();
        }

        const response = await fetchSampleCollectionWorklist(request);
        const encountersData = response.content || [];
        setPaginationFollowUp((prev) => ({ ...prev, total: response.totalElements || 0 }));

        const processedRows = encountersData
          .map((encounterData, index) => {
            const normalizedEncounterData = normalizeFollowUpEncounterData(encounterData);
            const { encounter, patient } = normalizedEncounterData;

            if (!encounter || !patient) {
              console.warn("Skipping invalid follow-up encounter data");
              return null;
            }

            return {
              id: encounter.encounterId || `followup-${index}-${Date.now()}`,
              patientName: `${patient.firstName || "Unknown"} ${patient.lastName || "Patient"}`,
              dateTime: formatDateTime(encounter.checkInDateTime),
              followUpId: encounter.followUpId,
              displayFollowUpCode: encounter.displayFollowUpCode,
              milestone: getMilestoneCode(encounter.displayFollowUpCode),
              isCollected: normalizedEncounterData.isCollected || false,
              encounterData: normalizedEncounterData,
            };
          })
          .filter((row) => row !== null);

        setFollowUpRows(processedRows);
      } catch (error) {
        console.error("Error fetching follow-up encounters:", error);
        setFollowUpRows([]);
      } finally {
        setLoadingFollowUp(false);
      }
    };
    fetchFollowUpData();
  }, [activeView, followUpDateFilters, paginationFollowUp.page, paginationFollowUp.pageSize, sortModelFollowUp]);

  const applyDateFilters = (nextFilters) => {
    const normalizedFilters = {
      encounterDateFrom: nextFilters.encounterDateFrom || "",
      encounterDateTo: nextFilters.encounterDateTo || "",
    };
    if (activeView === "followup") {
      setFollowUpDateFilters(normalizedFilters);
      writeStoredDateFilters(FOLLOW_UP_COLLECTION_DATE_FILTER_STORAGE_KEY, normalizedFilters);
    } else {
      setDateFilters(normalizedFilters);
      writeStoredDateFilters(SAMPLE_COLLECTION_DATE_FILTER_STORAGE_KEY, normalizedFilters);
    }
  };

  const resetDateFilters = (nextFilters) => {
    if (activeView === "followup") {
      setFollowUpDateFilters(nextFilters);
      writeStoredDateFilters(FOLLOW_UP_COLLECTION_DATE_FILTER_STORAGE_KEY, nextFilters);
    } else {
      setDateFilters(nextFilters);
      writeStoredDateFilters(SAMPLE_COLLECTION_DATE_FILTER_STORAGE_KEY, nextFilters);
    }
  };


  // Columns for "All Collections" view
  const allColumns = [
    {
      field: "patientName",
      headerName: "Participant Name",
      flex: 1,
    },
    {
      field: "dateTime",
      headerName: "Check-In Time",
      flex: 1,
    },
    {
      field: "encounterType",
      headerName: "Type",
      flex: 0.8,
      renderCell: ({ row }) => {
        if (row.encounterType === 'FOLLOW_UP') {
          const milestoneCode = getMilestoneCode(row.displayFollowUpCode);
          const milestoneNumber = formatMilestone(milestoneCode);
          return (
            <Chip
              label={`Follow Up ${milestoneNumber}`}
              size="small"
              clickable={false}
            onClick={(e) => e.stopPropagation()}
              sx={{
                ...BASE_CHIP_SX,
                ...getStatusChipStyle("FOLLOW_UP"),
                minWidth: 120,
                justifyContent: 'center',
                cursor: 'default',
                '&:hover': { backgroundColor: getStatusChipStyle("FOLLOW_UP").backgroundColor },
              }}
            />
          );
        }
        return (
          <Chip
            label="Regular"
            size="small"
            clickable={false}
            onClick={(e) => e.stopPropagation()}
            sx={{
              ...BASE_CHIP_SX,
              ...getStatusChipStyle("default"),
              minWidth: 120,
              justifyContent: 'center',
              cursor: 'default',
              '&:hover': { backgroundColor: getStatusChipStyle("default").backgroundColor },
            }}
          />
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      align: "center",
      renderCell: ({ row }) => {
        const encounter = row.encounterData?.encounter;
        const isFollowUp = encounter?.encounterType === 'FOLLOW_UP';
        const sampleCollected = row.isCollected;
        const isPatientCheckedIn = isCheckedIn(row.checkInStatus);

        // If sample is collected, show "COLLECTED" chip with edit icon
        if (sampleCollected) {
          return (
            <Box sx={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
              <Chip
                label="COLLECTED"
                size="small"
                sx={{
                  ...BASE_CHIP_SX,
                  ...getStatusChipStyle("COMPLETE"),
                  width: "120px",
                  cursor: "default",
                }}
              />
              {!isFollowUp && (
                <Box sx={{ position: "absolute", left: "calc(50% + 60px + 4px)" }}>
                  <Tooltip title="Add Additional Collection">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(BIOBANK_SUB_ROUTES.SAMPLE_COLLECTION_PLAN(row.id));
                      }}
                      sx={{ color: "primary.main" }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
          );
        }

        // If patient is not checked in
        if (!isPatientCheckedIn) {
          return (
            <Chip
              label="NOT CHECKED IN"
              size="small"
              variant="outlined"
              sx={{
                ...BASE_CHIP_SX,
                ...getStatusChipStyle("NOT_CHECKED_IN"),
                width: "175px",
                cursor: "default",
              }}
            />
          );
        }

        // Show "Collect" button for collection
        const onClick = (e) => {
          e.stopPropagation();
          sessionStorage.removeItem("bioSpecimenFormData");
          const returnTo = window.location.pathname + window.location.search;

          if (isFollowUp) {
            navigate(BIOBANK_SUB_ROUTES.SAMPLE_COLLECTION_FOLLOWUP(row.id), {
              state: { ...row.encounterData, returnTo, returnUrl: returnTo },
            });
          } else if (isCheckedIn(row.checkInStatus)) {
            navigate(BIOBANK_SUB_ROUTES.SAMPLE_COLLECTION_PLAN(row.id), {
              state: { encounter: row.encounterData },
            });
          }
        };

        return (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
            <Button
              variant="contained"
              onClick={onClick}
              sx={{
                width: "120px",
                height: "32px",
                fontSize: "13px",
                fontWeight: 600,
                whiteSpace: "nowrap",
                borderRadius: "10px",
                padding: 0,
                textTransform: "uppercase",
              }}
            >
              Collect
            </Button>
          </Box>
        );
      },
    },
  ];

  // Columns for "Follow-Up" view
  const followUpColumns = [
    {
      field: "patientName",
      headerName: "Participant Name",
      flex: 1,
    },
    {
      field: "dateTime",
      headerName: "Check-In Time",
      flex: 1,
    },
    {
      field: "milestone",
      headerName: "Milestone",
      flex: 0.6,
      renderCell: ({ row }) => {
        return (
          <Chip
            label={row.milestone}
            variant="outlined"
            size="small"
            clickable={false}
            onClick={(e) => e.stopPropagation()}
            sx={{
              ...BASE_CHIP_SX,
              ...getStatusChipStyle("FOLLOW_UP"),
              minWidth: 80,
              justifyContent: 'center',
              cursor: 'default',
              '&:hover': { backgroundColor: getStatusChipStyle("FOLLOW_UP").backgroundColor },
            }}
          />
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      align: "center",
      renderCell: ({ row }) => {
        const sampleCollected = row.isCollected;
        const encounter = row.encounterData?.encounter;

        if (sampleCollected) {
          return (
            <Box sx={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
              <Chip
                label="SAMPLE COLLECTED"
                size="small"
                clickable={false}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  ...BASE_CHIP_SX,
                  ...getStatusChipStyle("COMPLETE"),
                  width: "175px",
                  cursor: "default",
                }}
              />
            </Box>
          );
        }

        const onClick = (e) => {
          e.stopPropagation();
          sessionStorage.removeItem("bioSpecimenFormData");
          const returnTo = window.location.pathname + window.location.search;

          navigate(BIOBANK_SUB_ROUTES.SAMPLE_COLLECTION_FOLLOWUP(row.id), {
            state: { ...row.encounterData, returnTo, returnUrl: returnTo },
          });
        };

        return (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
            <Button
              variant="contained"
              onClick={onClick}
              sx={{
                width: "175px",
                height: "32px",
                fontSize: "13px",
                fontWeight: 600,
                whiteSpace: "nowrap",
                borderRadius: "10px",
                padding: 0,
                textTransform: "uppercase",
              }}
            >
              Collect
            </Button>
          </Box>
        );
      },
    },
  ];

  const currentRows = activeView === "all" ? allRows : followUpRows;
  const currentColumns = activeView === "all" ? allColumns : followUpColumns;
  const currentLoading = activeView === "all" ? loadingAll : loadingFollowUp;

  return (
    <WorkspacePage>
      <PageHeader 
        title="Sample Collection" 
        tabs={
          <Tabs
            value={activeView}
            onChange={(e, newVal) => setActiveView(newVal)}
          >
            <Tab value="all" label="All Collections" data-tour-id="tour-collection-tab-all" />
            <Tab value="followup" label="Follow-Up" data-tour-id="tour-collection-tab-followup" />
          </Tabs>
        }
      />

      <Box data-tour-id="tour-collection-date-filter">
        <DateRangeFilter
          value={activeView === "followup" ? followUpDateFilters : dateFilters}
          defaultValue={activeView === "followup" ? defaultFollowUpDateFilters : defaultDateFilters}
          onSearch={applyDateFilters}
          onReset={resetDateFilters}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: "100%",
          "& .MuiDataGrid-cell": {
            paddingLeft: "12px",
            paddingRight: "12px",
            backgroundColor: "#FFFFFF",
            display: "flex",
            alignItems: "center",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#F5F5F5",
          },
        }}
        data-tour-id="tour-collection-table"
      >
        <StyledDataGrid
          loading={currentLoading}
          rows={currentRows}
          columns={currentColumns}
          pageSizeOptions={[10, 25, 50, 100]}
          pagination
          paginationMode="server"
          sortingMode="server"
          rowCount={activeView === "all" ? paginationAll.total : paginationFollowUp.total}
          paginationModel={activeView === "all" ? { page: paginationAll.page, pageSize: paginationAll.pageSize } : { page: paginationFollowUp.page, pageSize: paginationFollowUp.pageSize }}
          onPaginationModelChange={(model) => {
            if (activeView === "all") {
              setPaginationAll((prev) => ({ ...prev, page: model.page, pageSize: model.pageSize }));
            } else {
              setPaginationFollowUp((prev) => ({ ...prev, page: model.page, pageSize: model.pageSize }));
            }
          }}
          onSortModelChange={(model) => {
            if (activeView === "all") {
              setSortModelAll(model);
            } else {
              setSortModelFollowUp(model);
            }
          }}
          hideFooterSelectedRowCount
          getRowId={(row) => row.id}
        />
      </Box>
    </WorkspacePage>
  );
};

export default SampleCollection;
